import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { unPadStoreNumber } from '../../utils/formatting';
import { getRetailAuthProfile, submitDevOpsUserSetup } from '../../utils/service-calls/rap-retail-auth';
import {
  ButtonBlack, ButtonSubmit, ButtonWhite, Select, StoreSelect,
} from '../reusable';

import { JOB_TITLES, MAX_STORES, MAX_STORES_MESSAGE } from './constants';
import ProfileSummary from './profile-summary';

const storeLabel = (store) => `${unPadStoreNumber(store.storeNumber)} - ${store.name}`;

const Main = ({ accessToken }) => {
  const [selectedCountry, setSelectedCountry] = useState({});
  const [selectedStores, setSelectedStores] = useState([]);
  const [jobTitle, setJobTitle] = useState(null);

  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }
    let cancelled = false;
    setProfileLoading(true);
    setProfileError('');
    getRetailAuthProfile(accessToken)
      .then(({ body }) => {
        if (!cancelled) {
          setProfile(body || null);
          setProfileLoading(false);
        }
        return body;
      })
      .catch((err) => {
        if (!cancelled) {
          setProfileError(err?.message || 'Failed to load Retail Auth profile.');
          setProfileLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [accessToken]);

  const resetForm = () => {
    setSelectedCountry({});
    setSelectedStores([]);
    setJobTitle(null);
  };

  const openForm = () => {
    setSubmitError('');
    setSuccessMessage('');
    setShowForm(true);
  };

  const cancelForm = () => {
    resetForm();
    setSubmitError('');
    setShowForm(false);
  };

  const isFormComplete = Boolean(selectedCountry?.value)
    && selectedStores.length > 0
    && selectedStores.length <= MAX_STORES
    && Boolean(jobTitle)
    && Boolean(profile)
    && !profileLoading
    && !profileError;

  const handleSubmit = () => {
    setLoading(true);
    setSubmitError('');
    setSuccessMessage('');

    const body = {
      country: selectedCountry.value,
      jobTitle,
      storeNumbers: selectedStores.map((store) => unPadStoreNumber(store.storeNumber)),
    };

    return submitDevOpsUserSetup(accessToken, body)
      .then(({ body: response }) => {
        setLoading(false);
        setSuccessMessage('DevOps user provisioned successfully.');
        resetForm();
        setShowForm(false);
        return response;
      })
      .catch((err) => {
        setLoading(false);
        setSubmitError(err?.message || 'Failed to submit DevOps user setup.');
      });
  };

  const canRequestAccess = Boolean(profile) && !profileLoading && !profileError;

  return (
    <main className="ncss-row">
      {profileLoading && (
        <p className="ncss-col-sm-12 body-3 ta-sm-c">Checking Retail Auth access...</p>
      )}
      {profileError && (
        <p className="ncss-col-sm-12 text-color-error body-3 ta-sm-c">{profileError}</p>
      )}
      {!profileLoading && !profileError && profile && (
        <ProfileSummary profile={profile} />
      )}

      {canRequestAccess && !showForm && (
        <article className="ncss-col-sm-12 ta-sm-c mt4-sm">
          <ButtonBlack
            label="Request Access"
            onClick={openForm}
          />
        </article>
      )}

      {showForm && (
        <>
          <article className="ncss-col-sm-6 ta-sm-l va-sm-t">
            <StoreSelect
              fullWidth
              isMulti
              isRequired
              maxSelections={MAX_STORES}
              maxSelectionsMessage={MAX_STORES_MESSAGE}
              selectCountry={(country) => {
                setSelectedCountry(country || {});
                setSelectedStores([]);
              }}
              selectStore={setSelectedStores}
              selectedCountry={selectedCountry}
              selectedStores={selectedStores}
              storeHeaderLabel="Store: Number - Name"
              storeLabel={storeLabel}
            />
          </article>
          <br />
          <Select
            className="ncss-col-sm-6 ta-sm-l va-sm-t mt2-sm"
            id="rap-devops-job-title"
            label="Job Title"
            options={JOB_TITLES}
            placeholder="Select a job title"
            value={jobTitle}
            onChange={setJobTitle}
          />

          <ButtonSubmit
            className="ncss-col-sm-12 ta-sm-c mt4-sm"
            isDisabled={!isFormComplete}
            isLoading={loading}
            // a successful submit clears the form, which would otherwise immediately
            // re-trigger the "resolve the remaining errors" hint under the banner
            showErrorMessage={!successMessage}
            submitError={submitError}
            onClick={handleSubmit}
          />

          <article className="ncss-col-sm-12 ta-sm-c">
            <ButtonWhite
              className="mt2-sm border"
              isDisabled={loading}
              label="Cancel"
              onClick={cancelForm}
            />
          </article>
        </>
      )}

      {successMessage && (
        <p className="ncss-col-sm-12 text-color-success body-2 mt4-sm ta-sm-c">{successMessage}</p>
      )}
    </main>
  );
};

Main.defaultProps = {
  accessToken: '',
};

Main.propTypes = {
  accessToken: PropTypes.string,
};

const mapStateToProps = (state) => ({
  accessToken: state.authorizationReducer.auth.accessToken,
});

export default connect(mapStateToProps, null)(Main);
